import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  CircularProgress,
  Alert,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Image as ImageIcon,
  Reply as ReplyIcon,
  ChatBubbleOutline as ChatIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { Feedback, FeedbackReply } from '../types';

const FeedbackChat: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleImageError = (url: string) => {
    setImageErrors(prev => new Set(prev).add(url));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  // Handle browser back button to close image dialog
  useEffect(() => {
    if (!imageDialogOpen) return;

    const handlePopState = () => {
      setImageDialogOpen(false);
    };

    // Push history state when dialog opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [imageDialogOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedFeedback]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getFeedbacks();
      
      if (response.status && response.doc) {
        setFeedbacks(response.doc);
        // Select the first feedback if none is selected
        if (response.doc.length > 0 && !selectedFeedback) {
          setSelectedFeedback(response.doc[0]);
        }
      } else {
        setError(response.message || 'Failed to fetch feedbacks');
      }
    } catch (err) {
      setError('An error occurred while fetching feedbacks');
      console.error('Error fetching feedbacks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!selectedFeedback || !replyText.trim()) return;

    try {
      setReplying(true);
      setError(null);
      
      // Try the primary reply method first
      let response = await apiService.replyToFeedback(selectedFeedback._id, replyText);
      
      // If the primary method fails, try the alternative method
      if (!response.status && response.message?.includes('404') || response.message?.includes('not found')) {
        console.log('Primary reply method failed, trying alternative...');
        response = await apiService.replyToFeedbackAlternative(selectedFeedback._id, replyText);
      }
      
      if (response.status) {
        // Create new reply object with timestamp
        const newReply = {
          reply: replyText,
          createdAt: Date.now()
        };

        // Update the local state with the new reply
        const updatedFeedbacks = feedbacks.map(feedback => 
          feedback._id === selectedFeedback._id 
            ? { ...feedback, replies: [...feedback.replies, newReply] }
            : feedback
        );
        setFeedbacks(updatedFeedbacks);
        
        // Update the selected feedback
        const updatedSelectedFeedback = {
          ...selectedFeedback,
          replies: [...selectedFeedback.replies, newReply]
        };
        setSelectedFeedback(updatedSelectedFeedback);
        
        setReplyText('');
        
        // Refresh the feedback list to get the latest data from server
        setTimeout(() => {
          fetchFeedbacks();
        }, 1000);
      } else {
        setError(response.message || 'Failed to send reply. The reply endpoint might not be implemented yet.');
      }
    } catch (err) {
      setError('An error occurred while sending reply. Please check your connection and try again.');
      console.error('Error sending reply:', err);
    } finally {
      setReplying(false);
    }
  };

  const handleImageClick = (images: string[]) => {
    setSelectedImages(images);
      window.history.pushState({ modalOpen: true }, '');
      setImageDialogOpen(true);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getInitials = (text: string) => {
    return text.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Customer Feedback Chat
      </Typography>

      <Grid container spacing={3} sx={{ flexGrow: 1, minHeight: 0 }}>
        {/* Feedback List Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box p={2} borderBottom="1px solid #e0e0e0">
              <Typography variant="h6" component="h2">
                Feedback List
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {feedbacks.length} feedback(s)
              </Typography>
            </Box>
            
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
              {feedbacks.map((feedback) => (
                <Card
                  key={feedback._id}
                  sx={{
                    mb: 1,
                    cursor: 'pointer',
                    border: selectedFeedback?._id === feedback._id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: 2,
                    },
                  }}
                  onClick={() => setSelectedFeedback(feedback)}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                        {getInitials(feedback.review)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 1,
                          }}
                        >
                          {feedback.review}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={`${feedback.replies.length} replies`}
                            size="small"
                            color={feedback.replies.length > 0 ? 'primary' : 'default'}
                            icon={<ReplyIcon />}
                          />
                          
                          {feedback.images.length > 0 && (
                            <Chip
                              label={`${feedback.images.length} image(s)`}
                              size="small"
                              color="secondary"
                              icon={<ImageIcon />}
                            />
                          )}
                          
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(feedback.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              
              {feedbacks.length === 0 && (
                <Box textAlign="center" p={3}>
                  <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No feedback available
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Chat Area */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedFeedback ? (
              <>
                {/* Chat Header */}
                <Box p={2} borderBottom="1px solid #e0e0e0">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: '#1976d2' }}>
                      {getInitials(selectedFeedback.review)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        Customer Feedback
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(selectedFeedback.createdAt)}
                      </Typography>
                    </Box>
                    {selectedFeedback.images.length > 0 && (
                      <Tooltip title="View images">
                        <IconButton
                          onClick={() => handleImageClick(selectedFeedback.images)}
                          color="primary"
                        >
                          <Badge badgeContent={selectedFeedback.images.length} color="secondary">
                            <ImageIcon />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* Messages */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                  {/* Customer Message */}
                  <Box display="flex" justifyContent="flex-start" mb={2}>
                    <Box display="flex" alignItems="flex-start" gap={1} maxWidth="80%">
                      <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                        {getInitials(selectedFeedback.review)}
                      </Avatar>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: '16px 16px 16px 4px',
                        }}
                      >
                        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                          {selectedFeedback.review}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Customer • {formatDate(selectedFeedback.createdAt)}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>

                  {/* Replies */}
                  {selectedFeedback.replies.map((replyObj, index) => (
                    <Box key={index} display="flex" justifyContent="flex-end" mb={2}>
                      <Box display="flex" alignItems="flex-start" gap={1} maxWidth="80%">
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: '#e3f2fd',
                            borderRadius: '16px 16px 4px 16px',
                          }}
                        >
                          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                            {replyObj.reply}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            You • {formatDate(replyObj.createdAt)}
                          </Typography>
                        </Paper>
                        <Avatar sx={{ bgcolor: '#4caf50', width: 32, height: 32 }}>
                          {getInitials('You')}
                        </Avatar>
                      </Box>
                    </Box>
                  ))}
                  
                  <div ref={messagesEndRef} />
                </Box>

                <Divider />

                {/* Reply Input */}
                <Box p={2}>
                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleReply();
                        }
                      }}
                      disabled={replying}
                    />
                    <Button
                      variant="contained"
                      onClick={handleReply}
                      disabled={!replyText.trim() || replying}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      {replying ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SendIcon />
                      )}
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                p={3}
              >
                <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a feedback to start chatting
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a feedback from the list to view details and reply
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => {
          if (window.history.state?.modalOpen) {
            window.history.back();
          }
          setImageDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Feedback Images</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {selectedImages.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                {imageErrors.has(image) ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Image not found
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={image}
                    alt={`Feedback image ${index + 1}`}
                    onError={() => handleImageError(image)}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    onClick={() => window.open(image, '_blank')}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (window.history.state?.modalOpen) {
                window.history.back();
              }
              setImageDialogOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedbackChat;
